import { ErrorModal } from "@dexteel/mesf-core";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useReportPdfExport } from "@/shared/hooks/useReportPdfExport";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";
import {
	useAccountSummary,
	useCloseDates,
	useCostCenters,
} from "../hooks/useSummaryByCloseDate";
import { toCloseDateOptions } from "../models/SummaryByCloseDate";
import { getAccountSummaryByClosePdf } from "../repositories/SummaryByCloseDateRepository";
import { AccountSummaryGrid } from "./AccountSummaryGrid";
import { AccountSummaryHeader } from "./AccountSummaryHeader";
import { SummaryByCloseDateToolbar } from "./SummaryByCloseDateToolbar";

// An empty state fills the space the sheet would take, instead of leaving a short card
// floating over the background with the page half grey underneath it.
const FILLER_SX = {
	flex: 1,
	minHeight: 0,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
} as const;

const URL_SCHEMA = {
	costCenterId: urlParam.int(),
	closeDate: urlParam.string(),
};

// The procedure speaks MM/DD/YYYY; the URL carries the date as YYYY-MM-DD like every
// other page.
const toUrlDate = (value: string | null) => {
	const [month, day, year] = value?.split("/") ?? [];
	return year && month && day ? `${year}-${month}-${day}` : null;
};
const fromUrlDate = (value: string | null) => {
	const [year, month, day] = value?.split("-") ?? [];
	return year && month && day ? `${month}/${day}/${year}` : null;
};

export const SummaryByCloseDateReport = () => {
	const [
		{ costCenterId, closeDate: urlCloseDate },
		setFilters,
		{ resetValues },
	] = useUrlState(URL_SCHEMA);
	const closeDate = fromUrlDate(urlCloseDate);
	const [errorMsg, setErrorMsg] = useState("");

	const costCentersQuery = useCostCenters();
	const costCenters = useMemo(
		() => costCentersQuery.data ?? [],
		[costCentersQuery.data],
	);
	// A link can name a cost center or a close that is not offered. Nothing is asked
	// for until the value is one of the options, so no request goes out with it.
	const knownCostCenterId =
		costCenterId != null && costCenters.some((cc) => cc.Id === costCenterId)
			? costCenterId
			: null;
	const closeDatesQuery = useCloseDates(knownCostCenterId);

	const closeDates = useMemo(
		() => toCloseDateOptions(closeDatesQuery.data ?? []),
		[closeDatesQuery.data],
	);
	const knownCloseDate =
		closeDate && closeDates.includes(closeDate) ? closeDate : null;
	const summaryQuery = useAccountSummary(knownCostCenterId, knownCloseDate);

	// Opening the page, or resetting it, lands on the first cost center and its latest
	// close. Picking a cost center by hand lands on its latest close. Choosing "Choose..."
	// is left alone, so the placeholder still clears a filter.
	const autoPick = useRef(true);

	const handleCostCenterChange = (value: number | null) => {
		// The close dates belong to the cost center, so the one on screen no longer
		// means anything.
		autoPick.current = value != null;
		setFilters({ costCenterId: value, closeDate: null });
	};

	const setCloseDate = (value: string | null) => {
		autoPick.current = false;
		setFilters({ closeDate: toUrlDate(value) });
	};

	const handleReset = () => {
		autoPick.current = true;
		resetValues();
	};

	useEffect(() => {
		if (!autoPick.current || !costCentersQuery.isSuccess) return;
		if (costCenterId == null) {
			if (costCenters.length === 0) {
				autoPick.current = false;
				return;
			}
			setFilters({ costCenterId: costCenters[0].Id, closeDate: null });
			return;
		}
		// An unknown cost center from a link is dropped by the check below first.
		if (knownCostCenterId == null || !closeDatesQuery.isSuccess) return;
		if (urlCloseDate != null) {
			// A close date from the link that the cost center has is kept; one it
			// does not have is dropped below, and then the latest is picked.
			if (knownCloseDate) autoPick.current = false;
			return;
		}
		autoPick.current = false;
		if (closeDates.length > 0)
			setFilters({ closeDate: toUrlDate(closeDates[0]) });
	}, [
		costCentersQuery.isSuccess,
		costCenters,
		costCenterId,
		knownCostCenterId,
		closeDatesQuery.isSuccess,
		closeDates,
		knownCloseDate,
		urlCloseDate,
		setFilters,
	]);

	useEffect(() => {
		if (!costCentersQuery.isSuccess || costCenterId == null) return;
		if (knownCostCenterId == null)
			setFilters({ costCenterId: null, closeDate: null });
	}, [costCentersQuery.isSuccess, costCenterId, knownCostCenterId, setFilters]);

	useEffect(() => {
		if (urlCloseDate == null) return;
		if (costCenterId == null) {
			setFilters({ closeDate: null });
			return;
		}
		if (closeDatesQuery.isSuccess && !knownCloseDate)
			setFilters({ closeDate: null });
	}, [
		closeDatesQuery.isSuccess,
		knownCloseDate,
		urlCloseDate,
		costCenterId,
		setFilters,
	]);

	const error =
		costCentersQuery.error ?? closeDatesQuery.error ?? summaryQuery.error;
	if (error && !errorMsg) setErrorMsg((error as Error).message);

	const summary = summaryQuery.data ?? null;
	const selectionComplete = knownCostCenterId != null && knownCloseDate != null;
	const noCostCenters = costCentersQuery.isSuccess && costCenters.length === 0;
	const noCloses =
		knownCostCenterId != null &&
		closeDatesQuery.isSuccess &&
		closeDates.length === 0;
	// While the defaults are being picked, or a link is being checked against the
	// options, the page waits instead of asking the user to pick.
	const settling =
		!selectionComplete &&
		!noCostCenters &&
		!noCloses &&
		!costCentersQuery.isError &&
		!closeDatesQuery.isError &&
		(autoPick.current || (costCenterId != null && closeDate != null));

	const atDefaults =
		knownCostCenterId != null &&
		knownCostCenterId === costCenters[0]?.Id &&
		knownCloseDate != null &&
		knownCloseDate === closeDates[0];
	const canReset = costCenters.length > 0 && !atDefaults;

	const { exporting: exportingPdf, exportPdf } =
		useReportPdfExport(setErrorMsg);
	const handleExportPdf = () => {
		if (knownCostCenterId == null || !knownCloseDate || !summary) return;
		exportPdf(
			`Summary By Close Date ${summary.CostCenterName?.trim() ?? knownCostCenterId} ${knownCloseDate.replaceAll("/", "-")}.pdf`,
			() => getAccountSummaryByClosePdf(knownCostCenterId, knownCloseDate),
		);
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				// The app navbar is 64px tall and nothing above this Box reserves height, so
				// the page has to claim the rest itself.
				height: "calc(100vh - 64px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Summary By Close Date
			</Typography>

			<Paper elevation={3}>
				<SummaryByCloseDateToolbar
					onReset={handleReset}
					canReset={canReset}
					costCenters={costCenters}
					costCenterId={knownCostCenterId}
					onCostCenterChange={handleCostCenterChange}
					closeDates={closeDates}
					closeDate={knownCloseDate}
					onCloseDateChange={setCloseDate}
					closeDatesLoading={closeDatesQuery.isInitialLoading}
					onExportPdf={handleExportPdf}
					canExportPdf={!!summary && selectionComplete}
					exportingPdf={exportingPdf}
				/>
			</Paper>

			{!selectionComplete && !settling && (
				<Paper elevation={3} sx={FILLER_SX}>
					<Typography variant="body1" color="text.secondary">
						{noCostCenters
							? "No cost centers available"
							: noCloses
								? "No closes saved for this cost center"
								: "Pick a cost center and a close date"}
					</Typography>
				</Paper>
			)}

			{((selectionComplete && summaryQuery.isInitialLoading) || settling) && (
				<Paper elevation={3} sx={FILLER_SX}>
					<CircularProgress />
				</Paper>
			)}

			{selectionComplete && !summaryQuery.isInitialLoading && !summary && (
				<Paper elevation={3} sx={FILLER_SX}>
					<Typography variant="body1" color="text.secondary">
						No summary for the selected cost center and close date
					</Typography>
				</Paper>
			)}

			{selectionComplete && !summaryQuery.isInitialLoading && summary && (
				<AccountSummaryGrid
					summary={summary}
					header={<AccountSummaryHeader summary={summary} />}
				/>
			)}

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
